import React from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { alpha, useTheme } from '@mui/material/styles'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { formatDuration } from '../duration'
import { timeVsTheoretical } from '../timeVsTheoretical'
import { openIncidences } from '../operationRowFlags'
import { isBlank } from '../detailShared'

// S431 (plan «Workflow teórico · Progress real», 23/09) — las dos celdas REALES de una fila de
// operación: las cuatro cifras y el tiempo contra el teórico. Vivían en la fila de Workflow y se
// MUDAN a la de Progress (D1, D6); se sacan aquí con su texto, sus reglas y sus `data-testid` tal
// cual, para que moverlas no las reescriba. Reciben una fila con los nombres de N3
// (`quantityDone`, `quantityPending`…); Progress le traduce los suyos de N10 al llamarlas.

// §2.4 — toda cifra y duración en la fuente de números: alinea la columna consigo misma.
export const NUMERIC_FONT = { fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }

// ADDENDUM 11 (E17)/RN8: `done` y `pending` a cero a la vez no es "cero producido", es que la pieza
// NUNCA llegó a esta operación. Decirlo con números afirmaría algo falso sobre una rama no tomada.
const quantityNeverReached = (row) => Number(row.quantityDone) === 0 && Number(row.quantityPending) === 0 &&
  !isBlank(row.quantityDone) && !isBlank(row.quantityPending)

const QuantityFigure = ({ testId, value, tone }) => {
  const ausente = value === null || value === undefined || value === ''
  return (
    <Box component='span' data-testid={testId} data-absent={ausente ? 'true' : 'false'} sx={{ fontWeight: 600, color: ausente ? 'text.disabled' : (tone || 'text.primary') }}>
      {ausente ? '—' : value}
    </Box>
  )
}

const QuantitySlash = () => (
  <Box component='span' aria-hidden='true' sx={{ mx: 0.5, color: 'text.disabled', fontWeight: 400 }}>/</Box>
)

export const OperationQuantityCell = ({ row }) => {
  const { t_ } = useSmartTranslation()
  const incidencias = openIncidences(row)
  return (
    <>
      {/* `0` y `0` juntos NO son dos ceros que contar: significan que la pieza
          nunca llegó a esta operación —ningún predecesor reportó hacia ese nodo,
          rama no tomada—. Un `0` acompañado de un número distinto de cero SÍ es un
          cero real y se muestra.
          ⚠ ADDENDUM 30 A: esa celda decía "Pendiente" y ahora dice "No calculable",
          la misma clave que el porcentaje de avance cuando nada llegó al nodo (RN8,
          S319). Las dos son la MISMA imposibilidad aritmética, y es distinta de que
          el dato no haya llegado: por eso los huecos de al lado dicen "Sin informar"
          y esta celda no. */}
      {/* ⚠️ EL `title` LISTA LAS CUATRO CON SU NOMBRE, y no es un extra: en la
          celda son cuatro cifras separadas por barras, y cuál es cuál solo lo
          dice la cabecera, una vez, arriba del todo. Quien mire una fila a media
          tabla necesita poder preguntárselo sin subir. Aquí SÍ se distingue
          ausente de cero, porque es donde se lee el dato (RN8). */}
      <Typography
        data-testid='operation-quantity'
        variant='body2'
        title={[
          `${t_('Reported OK')}: ${isBlank(row.quantityDone) ? t_('Not reported') : row.quantityDone}`,
          `${t_('Left to report')}: ${isBlank(row.quantityPending) ? t_('Not reported') : row.quantityPending}`,
          // ⚠️ El destino (SCRAP/REWORK) solo se nombra si LLEGA: N10 no lo sirve, y escribir
          // «UNKNOWN» en su lugar afirmaría un valor real del enum que el dato no dice.
          `${t_('Rejected')}: ${isBlank(row.rejectedQuantity) ? t_('Not reported') : isBlank(row.rejectedDisposition) ? row.rejectedQuantity : `${row.rejectedQuantity} (${t_(row.rejectedDisposition)})`}`,
          `${t_('Open incidences')}: ${isBlank(row.openIncidenceCount) ? t_('Not reported') : row.openIncidenceCount}`
        ].join(' · ')}
        // Task 29 (D-A6): the four figures stay on one line; «Not computable» may wrap — on one line
        // it set the column's width well above its minimum.
        sx={{ whiteSpace: quantityNeverReached(row) ? 'normal' : 'nowrap', ...NUMERIC_FONT, fontSize: 15 }}
      >
        {quantityNeverReached(row)
          ? t_('Not computable')
          : (
            <>
              <QuantityFigure testId='operation-qty-ok' value={row.quantityDone} tone={Number(row.quantityDone) >= 1 ? 'success.main' : undefined} />
              <QuantitySlash />
              <QuantityFigure testId='operation-qty-pending' value={row.quantityPending} />
              <QuantitySlash />
              <QuantityFigure testId='operation-qty-rejected' value={row.rejectedQuantity} tone={Number(row.rejectedQuantity) >= 1 ? 'error.main' : undefined} />
              <QuantitySlash />
              <QuantityFigure testId='operation-qty-incidences' value={row.openIncidenceCount} tone={incidencias >= 1 ? 'error.main' : undefined} />
            </>
            )}
      </Typography>
    </>
  )
}

export const OperationTimeCell = ({ row }) => {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const tiempo = timeVsTheoretical(row.theoreticalDurationSeconds, row.actualDurationSeconds)
  const tonoTiempo = tiempo.over ? theme.palette.warning.main : theme.palette.success.main
  // ADDENDUM 44 — LA BARRA INDICA, EL NÚMERO MIDE. El ancho está saturado a propósito y el porqué
  // está entero en `timeVsTheoretical.js`: el 44% de los teóricos positivos vale 1 segundo, así que
  // un ratio fiel daría anchos de seis cifras. El teórico se pinta debajo, en gris, para que quien
  // vea "+5h 12'" sobre un teórico de 1 s pueda juzgar el dato en vez de creérselo (RN8).
  // ⚠️⚠️ DOS TRAMOS APILADOS, NO UN RATIO: con el 44% de los teóricos valiendo 1 segundo, un ratio
  // saturado salía lleno e igual en todas esas filas. Apilando no hay divisor que explote.
  // 📌 Esta barra NO es una de las «barras de color» que retiró S442: aquellas repetían el ESTADO
  // de la fila; ésta dibuja dos magnitudes, y el número de debajo las dice.
  // ⚠️⚠️ CUATRO CASOS, NO «LOS DOS O NADA». Esto pintaba solo si se podía COMPARAR, y con el teórico
  // ausente decía «No time data» con el tiempo real delante — lo vio el PO: la fila decía «sin datos»
  // mientras su desplegable enseñaba 1h 36m reales. RN8 al revés: un dato real escondido detrás de
  // uno ausente. Cada tiempo se enseña si EXISTE; lo que necesita a los dos es la barra y la
  // desviación, y solo eso se calla —diciéndolo— cuando falta uno.
  const hayReal = !isBlank(row.actualDurationSeconds)
  const hayTeorico = !isBlank(row.theoreticalDurationSeconds)
  return (
    <Box data-testid='operation-time-vs-theoretical' sx={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
      {!hayReal && !hayTeorico && (
        <Typography variant='caption' sx={{ color: 'text.secondary' }}>{t_('No time data')}</Typography>
      )}
      {tiempo.computable && (
        <Box data-testid='operation-time-bar' data-over={tiempo.over ? 'true' : 'false'} sx={{ display: 'flex', height: 8, borderRadius: '4px', backgroundColor: alpha(theme.palette.text.primary, 0.12), overflow: 'hidden', mb: 0.5 }}>
          <Box data-testid='operation-time-bar-theoretical' sx={{ width: `${tiempo.theoreticalFraction * 100}%`, backgroundColor: theme.palette.success.main }} />
          <Box data-testid='operation-time-bar-deviation' sx={{ width: `${tiempo.deviationFraction * 100}%`, backgroundColor: alpha(theme.palette.warning.main, 0.85) }} />
        </Box>
      )}
      {hayReal && (
        <Typography data-testid='operation-time-label' variant='caption' sx={{ display: 'block', ...NUMERIC_FONT, fontSize: 12, color: tiempo.computable ? tonoTiempo : 'text.primary', whiteSpace: 'normal', overflowWrap: 'anywhere', lineHeight: 1.3, fontWeight: 600 }}>
          {tiempo.computable
            ? `${formatDuration(row.actualDurationSeconds)} · ${tiempo.over ? `+${formatDuration(tiempo.deltaSeconds)}` : t_('Within theoretical')}`
            : formatDuration(row.actualDurationSeconds)}
        </Typography>
      )}
      {!hayReal && hayTeorico && (
        <Typography data-testid='operation-time-label' variant='caption' sx={{ display: 'block', color: 'text.secondary', whiteSpace: 'normal', overflowWrap: 'anywhere', lineHeight: 1.3 }}>
          {t_('No actual time')}
        </Typography>
      )}
      {hayTeorico && (
        <Typography data-testid='operation-time-theoretical' variant='caption' sx={{ display: 'block', ...NUMERIC_FONT, fontSize: 11.5, color: 'text.secondary', whiteSpace: 'normal', overflowWrap: 'anywhere', lineHeight: 1.3 }}>
          {`${t_('theor.')} ${formatDuration(row.theoreticalDurationSeconds)}`}
        </Typography>
      )}
      {hayReal && !hayTeorico && (
        // Parte línea A PROPÓSITO: en una línea, esta nota ensanchaba la columna a 266 px (medido).
        <Typography data-testid='operation-time-theoretical' variant='caption' sx={{ display: 'block', color: 'text.secondary', whiteSpace: 'normal', lineHeight: 1.3 }}>
          {t_('No theoretical · deviation not computable')}
        </Typography>
      )}
    </Box>
  )
}
